import { useState, useEffect, useRef, useCallback } from 'react'
import { ChevronLeft, ChevronRight, Search, Loader2, X } from 'lucide-react'
import { brandKitApi } from '../../lib/api/brandKits'
import { BrandKit } from '../../types/brandKit'
import { useRole } from '../../hooks/useRole'

interface BrandKitPickerProps {
  selectedBrandKit: BrandKit | null
  onSelect: (brandKit: BrandKit | null) => void
  onClose: () => void
  advertiserId?: string
}

const ACCENT_COLORS = [
  ['#EF4444', '#F97316', '#6B7280'],
  ['#3B82F6', '#8B5CF6', '#EC4899'],
  ['#10B981', '#F59E0B', '#6366F1'],
  ['#14B8A6', '#F43F5E', '#A855F7'],
  ['#0EA5E9', '#D946EF', '#84CC16'],
  ['#E11D48', '#7C3AED', '#059669'],
]

function getAccentColors(name: string): string[] {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return ACCENT_COLORS[Math.abs(hash) % ACCENT_COLORS.length]
}

export function BrandKitPicker({ selectedBrandKit, onSelect, onClose, advertiserId }: BrandKitPickerProps) {
  const { isAdmin } = useRole()
  const isAdminUser = isAdmin()
  const [brandKits, setBrandKits] = useState<BrandKit[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)

  const scrollRef = useRef<HTMLDivElement>(null)
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null)

  const loadBrandKits = useCallback(async (query?: string) => {
    setIsLoading(true)
    try {
      const params: Record<string, any> = { per_page: 50 }
      if (query) params.q = query
      if (!isAdminUser && advertiserId) params.advertiser_id = advertiserId
      const { brand_kits } = await brandKitApi.getBrandKits(params)
      setBrandKits(brand_kits)
    } catch (err) {
      console.error('Failed to load brand kits:', err)
    } finally {
      setIsLoading(false)
    }
  }, [isAdminUser, advertiserId])

  useEffect(() => { loadBrandKits() }, [loadBrandKits])

  const handleSearchChange = (val: string) => {
    setSearchQuery(val)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => loadBrandKits(val), 300)
  }

  useEffect(() => () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }, [])

  const checkScrollArrows = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setShowLeftArrow(el.scrollLeft > 0)
    setShowRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }, [])

  useEffect(() => { checkScrollArrows() }, [brandKits, checkScrollArrows])

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: dir === 'left' ? -240 : 240, behavior: 'smooth' })
    setTimeout(checkScrollArrows, 350)
  }

  return (
    <div className="border border-gray-200/60 dark:border-gray-600/40 rounded-2xl bg-gray-50 dark:bg-[#253345] shadow-sm overflow-visible">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
          Select Brand kit
        </span>

        {/* Search */}
        <div className="flex-1 max-w-[220px] relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by name..."
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500"
          />
        </div>

        <div className="flex-1" />

        {/* Arrows */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => scroll('left')}
            disabled={!showLeftArrow}
            className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </button>
          <button
            type="button"
            onClick={() => scroll('right')}
            disabled={!showRightArrow}
            className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Carousel */}
      <div className="px-4 pb-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          </div>
        ) : brandKits.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
            No brand kits found
          </div>
        ) : (
          <div
            ref={scrollRef}
            onScroll={checkScrollArrows}
            className="flex gap-3 overflow-x-auto scrollbar-none py-1"
          >
            {brandKits.map((bk) => {
              const isSelected = selectedBrandKit?.id === bk.id
              const colors = getAccentColors(bk.name)

              return (
                <button
                  key={bk.id}
                  type="button"
                  onClick={() => onSelect(isSelected ? null : bk)}
                  className={`flex-shrink-0 w-[180px] rounded-xl border-2 p-3 text-left transition-all cursor-pointer ${
                    isSelected
                      ? 'border-green-500 dark:border-green-400 bg-white dark:bg-gray-800 shadow-md shadow-green-500/10'
                      : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/70 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm'
                  }`}
                >
                  {/* Brand name */}
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate mb-2.5">
                    {bk.name}
                  </p>

                  {/* Visual area: logo placeholder + font labels */}
                  <div className="flex gap-2 mb-3">
                    {/* Logo placeholder */}
                    <div className="w-14 h-14 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <span className="text-xl font-bold text-gray-400 dark:text-gray-500">
                        {bk.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    {/* Font tags */}
                    <div className="flex flex-col gap-1.5 min-w-0 justify-center">
                      <span className="inline-block px-2 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded truncate max-w-[80px]">
                        Sans Serif
                      </span>
                      <span className="inline-block px-2 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded truncate max-w-[80px]">
                        Serif
                      </span>
                    </div>
                  </div>

                  {/* Color dots */}
                  <div className="flex items-center gap-1.5">
                    {colors.map((color, i) => (
                      <div
                        key={i}
                        className="w-4 h-4 rounded-full border border-white dark:border-gray-600 shadow-sm"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
